export type WorkflowReadOnlyGraphNodeKind =
  | "entry"
  | "human-step"
  | "automated-step"
  | "routing-decision"
  | "terminal"
  | "missing-endpoint"
  | "invalid";

export type WorkflowReadOnlyGraphEdgeKind = "route" | "invalid";

export interface WorkflowReadOnlyGraphDimensions {
  readonly width: number;
  readonly height: number;
}

export const WORKFLOW_READ_ONLY_GRAPH_NODE_DIMENSIONS: WorkflowReadOnlyGraphDimensions = {
  width: 190,
  height: 78,
};

export const WORKFLOW_READ_ONLY_GRAPH_PLACEHOLDER_NODE_DIMENSIONS: WorkflowReadOnlyGraphDimensions = {
  width: 190,
  height: 72,
};

export const WORKFLOW_READ_ONLY_GRAPH_EDGE_LABEL_DIMENSIONS: WorkflowReadOnlyGraphDimensions = {
  width: 132,
  height: 32,
};

export interface WorkflowReadOnlyGraphFact {
  readonly label: string;
  readonly value: string;
}

export interface WorkflowReadOnlyGraphElementDetail {
  readonly title: string;
  readonly subtitle: string;
  readonly description: string;
  readonly facts: readonly WorkflowReadOnlyGraphFact[];
}

export const WORKFLOW_READ_ONLY_GRAPH_WORKFLOW_STAGES_LANE_ID =
  "responsibility-lane:workflow-stages";

export const WORKFLOW_READ_ONLY_GRAPH_WORKFLOW_REQUIREMENTS_LANE_ID =
  "responsibility-lane:workflow-requirements";

export type WorkflowReadOnlyGraphResponsibilityLaneKind =
  | "stage"
  | "workflow-requirements"
  | "queue-guidance"
  | "role-guidance"
  | "unassigned-guidance"
  | "mixed-guidance"
  | "needs-attention";

export type WorkflowReadOnlyGraphResponsibilityLaneScope =
  | "workflow-stages"
  | "workflow-wide"
  | "step-decisions";

/**
 * A presentation-only partition for the UML-adjacent workflow view.
 *
 * Queue and role lanes describe saved decision guidance. They never assert
 * live assignment, access, quorum, or step ownership.
 */
export interface WorkflowReadOnlyGraphResponsibilityLane {
  readonly id: string;
  readonly kind: WorkflowReadOnlyGraphResponsibilityLaneKind;
  readonly scope: WorkflowReadOnlyGraphResponsibilityLaneScope;
  readonly label: string;
  readonly description: string;
  readonly queueGuidanceCodes: readonly string[];
  readonly suggestedRoleCodes: readonly string[];
  readonly gateCount: number;
}

export type WorkflowReadOnlyGraphDecisionGateScopeKind =
  | "every-status-change"
  | "specific-status-change"
  | "multiple-status-changes"
  | "incomplete";

/** Exact saved selector used when opening one authored decision requirement. */
export interface WorkflowReadOnlyGraphDecisionGateOriginSelector {
  readonly sourceKind: "structured" | "legacy";
  readonly locationKind: "direct" | "transition";
  /** Null only when malformed collection evidence has no addressable saved row. */
  readonly decisionIndex: number | null;
  /** Null for a direct workflow/step requirement. */
  readonly transitionIndex: number | null;
}

/** Exact saved location contributing to one semantically grouped gate. */
export interface WorkflowReadOnlyGraphDecisionGateOrigin
  extends WorkflowReadOnlyGraphDecisionGateOriginSelector {
  readonly id: string;
  readonly scopeKind: "all" | "transition";
  readonly fromStateCode: string | null;
  readonly toStateCode: string | null;
  readonly queueGuidanceCode: string | null;
  readonly suggestedRoleCodes: readonly string[];
  readonly notificationCount: number;
  readonly isMalformed: boolean;
}

/**
 * Exact handoff from a decision diamond or its keyboard-equivalent action.
 *
 * A rendered gate can summarize equivalent requirements from more than one
 * saved location. The first authored origin is used deterministically; the
 * counts keep that grouping explicit to the receiving editor and user.
 */
export interface WorkflowReadOnlyGraphDecisionGateRequest {
  readonly ownerSourceNodeId: string | null;
  readonly ownerSourceNodeIndex: number | null;
  readonly gateId: string;
  readonly origin: WorkflowReadOnlyGraphDecisionGateOriginSelector;
  readonly authoredOriginCount: number;
  readonly representsGroupedOrigins: boolean;
}

/**
 * Opens the decision-rule collection for one workflow scope from the compact
 * overview. Unlike a routed decision-gate request, this intentionally does not
 * identify one saved rule: an overview summary can represent several rules.
 */
export interface WorkflowReadOnlyGraphDecisionScopeRequest {
  /** Null when the summary represents workflow-wide decision requirements. */
  readonly ownerSourceNodeId: string | null;
  /** Null when the summary represents workflow-wide decision requirements. */
  readonly ownerSourceNodeIndex: number | null;
}

/**
 * A display-only summary of one effective decision requirement at a human step.
 *
 * Gates describe status-change requirements. They are deliberately not graph
 * nodes or edges because the saved definition does not use decisions to route
 * work between authored workflow nodes.
 */
export interface WorkflowReadOnlyGraphDecisionGate {
  readonly id: string;
  readonly ownerLabel: string;
  readonly ownerSourceId: string;
  /** Original node index, or null when the gate belongs to the whole workflow. */
  readonly ownerSourceIndex: number | null;
  readonly title: string;
  /** Concise question suitable for a UML decision diamond. */
  readonly umlQuestion: string;
  readonly matchedOutcomeLabel: string;
  readonly matchedEffect: string;
  readonly blockedFallback: string;
  readonly scopeKind: WorkflowReadOnlyGraphDecisionGateScopeKind;
  readonly scopeLabel: string;
  /** Presentation lane only; it does not assign or authorize live work. */
  readonly responsibilityLaneId: string;
  readonly responsibilityLaneKind: WorkflowReadOnlyGraphResponsibilityLaneKind;
  readonly queueGuidanceCodes: readonly string[];
  readonly suggestedRoleCodes: readonly string[];
  readonly notificationCount: number;
  readonly isLegacyOnly: boolean;
  readonly authoredOrigins: readonly WorkflowReadOnlyGraphDecisionGateOrigin[];
  readonly effect: string;
  readonly accessibleDescription: string;
  readonly decisionKindCode: string | null;
  readonly requiredOutcomeCode: string | null;
  readonly reactionCode: string | null;
  readonly targetStateCode: string | null;
  readonly appliesToEveryStatusChange: boolean;
  readonly affectedTransitionCount: number;
  readonly authoredOriginCount: number;
  readonly malformedOriginCount: number;
  readonly isInvalid: boolean;
}

export function workflowDecisionGateRequest(
  gate: WorkflowReadOnlyGraphDecisionGate,
): WorkflowReadOnlyGraphDecisionGateRequest | null {
  const origin = gate.authoredOrigins[0];
  if (!origin) {
    return null;
  }

  return {
    ownerSourceNodeId: gate.ownerSourceIndex === null ? null : gate.ownerSourceId,
    ownerSourceNodeIndex: gate.ownerSourceIndex,
    gateId: gate.id,
    origin: {
      sourceKind: origin.sourceKind,
      locationKind: origin.locationKind,
      decisionIndex: origin.decisionIndex,
      transitionIndex: origin.transitionIndex,
    },
    authoredOriginCount: gate.authoredOriginCount,
    representsGroupedOrigins: gate.authoredOriginCount > 1,
  };
}

export interface WorkflowReadOnlyGraphNode {
  /** Renderer-safe ID. This is intentionally distinct from the authored stable ID. */
  readonly id: string;
  /** Exact stable ID from the draft, or the unresolved endpoint value for a placeholder. */
  readonly sourceId: string;
  /** Original index in the authored node collection. Placeholders have no authored index. */
  readonly sourceIndex: number | null;
  readonly kind: WorkflowReadOnlyGraphNodeKind;
  /**
   * All authored routed-workflow topology stays in the central stage lane.
   * Compact status-only projections may omit responsibility guidance.
   */
  readonly responsibilityLaneId?: string;
  readonly groupLabel: string;
  readonly label: string;
  readonly description: string;
  readonly width: number;
  readonly height: number;
  readonly displayOrder: number | null;
  /**
   * One-based human-step position when authored routes form one valid,
   * unambiguous entry-to-terminal chain. Other graph projections may omit it.
   */
  readonly processOrder?: number | null;
  readonly stepCode: string | null;
  /** Display-only status-change gates owned by this human step. */
  readonly decisionGates?: readonly WorkflowReadOnlyGraphDecisionGate[];
  readonly appliedTransitionCount: number;
  readonly appliedDecisionCount: number;
  readonly malformedTransitionCount: number;
  readonly malformedDecisionCount: number;
  readonly isInvalid: boolean;
  readonly detail: WorkflowReadOnlyGraphElementDetail;
}

export interface WorkflowReadOnlyGraphEdge {
  readonly pathKindCode?: string | null;
  readonly outcomeCode?: string | null;
  /** Renderer-safe ID. This is intentionally distinct from the authored stable ID. */
  readonly id: string;
  /** Exact stable edge ID from the draft. */
  readonly sourceId: string;
  /** Original index in the authored edge collection. */
  readonly sourceIndex: number;
  /** Renderer-safe endpoint IDs. */
  readonly source: string;
  readonly target: string;
  /** Exact authored endpoint values for accessible route tables and diagnostics. */
  readonly fromSourceNodeId: string;
  readonly toSourceNodeId: string;
  readonly fromDisplayValue: string;
  readonly toDisplayValue: string;
  readonly kind: WorkflowReadOnlyGraphEdgeKind;
  readonly groupLabel: string;
  readonly label: string;
  readonly resultLabel?: string;
  readonly description: string;
  readonly labelWidth: number;
  readonly labelHeight: number;
  readonly isInvalid: boolean;
  readonly detail: WorkflowReadOnlyGraphElementDetail;
}

export interface WorkflowReadOnlyGraphSelection {
  readonly kind: "node" | "edge";
  readonly rendererId: string;
  readonly sourceId: string;
  readonly sourceIndex: number | null;
  readonly isValid: boolean;
  /** Whether this exact authored element can safely be opened for editing. */
  readonly isAuthorable: boolean;
}

export interface WorkflowReadOnlyGraphSelectionRequest {
  readonly selection: WorkflowReadOnlyGraphSelection | null;
  readonly origin: "picker" | "visual";
  /** Keyboard selection should move focus into the contextual inspector once accepted. */
  readonly moveFocusToInspector: boolean;
}

export interface WorkflowReadOnlyGraph {
  /**
   * Deterministic identity for all content displayed by the graph and its
   * accessible detail/table projection.
   */
  readonly contentKey: string;
  readonly label: string;
  readonly description: string;
  readonly nodes: readonly WorkflowReadOnlyGraphNode[];
  readonly edges: readonly WorkflowReadOnlyGraphEdge[];
  readonly authoredNodeCount: number;
  readonly placeholderNodeCount: number;
  readonly appliedTransitionCount: number;
  readonly appliedDecisionCount: number;
  /**
   * Presentation-only responsibility guidance. The central workflow lane is
   * always present. Workflow-wide requirements remain a distinct band rather
   * than being assigned to a step decision lane.
   */
  readonly responsibilityLanes?: readonly WorkflowReadOnlyGraphResponsibilityLane[];
  /**
   * Display-only workflow-scope status-change gates, including malformed saved
   * evidence that cannot be counted as an effective gate.
   */
  readonly workflowDecisionGates?: readonly WorkflowReadOnlyGraphDecisionGate[];
  /**
   * Effective workflow-scope gates after semantic grouping. This is a count,
   * not additional map topology. Other graph projections may omit it.
   */
  readonly workflowDecisionGateCount?: number;
  readonly malformedEntryCount: number;
  readonly invalidElementCount: number;
}
